import { useCallback, useMemo, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  ClickExpandBlock,
  ClickExpandImageSize,
  ClickExpandTitleLevel,
  ClickExpandTopic,
} from '@course-authoring/shared-types';
import {
  CLICK_EXPAND_MAX_TOPICS,
  CLICK_EXPAND_MIN_TOPICS,
  canUseTopTabsClickExpandLayout,
  getClickExpandMaxTopics,
  useTheme,
} from '@course-authoring/shared-types';
import { createContentBlockStyles, useIsMobileViewport } from '@course-authoring/ui-blocks';

import { ClickExpandRichFieldEditor } from './ClickExpandRichFieldEditor';
import { ClickExpandTopicEditor } from './ClickExpandTopicEditor';
import {
  clickExpandEditorInnerStyles,
  createClickExpandEditorStyles,
} from './clickExpandEditorStyles';
import type { ClickExpandFieldName } from './clickExpandEditorTypes';
import { EMPTY_RICH_TEXT_DOC, updateById } from './interactionEditorUtils';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import { ImageInsertModal } from './ImageInsertModal';
import type { ImageInsertData } from './ImageInsertModal';
import { useImageInsertTarget } from './useImageInsertTarget';

export interface ClickExpandEditorProps {
  block: ClickExpandBlock;
  onChange: (updates: Partial<ClickExpandBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function ClickExpandEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: ClickExpandEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const titleLevel = block.titleLevel ?? 'h2';
  const [focusedField, setFocusedField] = useState<ClickExpandFieldName | null>(null);
  const {
    target: imageModalTopicId,
    isOpen: isImageModalOpen,
    openTarget: openImageModalTarget,
    clearTarget: clearImageModalTarget,
  } = useImageInsertTarget<string>();
  const containerRef = useRef<HTMLDivElement>(null);

  const isFocused = focusedField !== null;
  const editorStyles = useMemo(() => {
    const baseStyles = createContentBlockStyles(tokens);
    return createClickExpandEditorStyles(tokens, baseStyles, isFocused, isMobile);
  }, [isFocused, isMobile, tokens]);
  const maxTopics = getClickExpandMaxTopics(block.expandType);
  const hasReachedMaxTopics = block.topics.length >= maxTopics;
  const canSwitchToTopTabs = canUseTopTabsClickExpandLayout(block.topics.length);
  const isTopTabsSelectionDisabled = block.expandType !== 'top-tabs' && !canSwitchToTopTabs;

  const handleFieldFocus = useCallback((fieldName: ClickExpandFieldName) => {
    setFocusedField(fieldName);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  const handleDescriptionChange = useCallback(
    (content: JSONContent) => {
      onChange({ description: content });
    },
    [onChange]
  );

  const handleExpandTypeChange = useCallback(
    (expandType: 'accordion' | 'top-tabs') => {
      if (expandType === 'top-tabs' && !canUseTopTabsClickExpandLayout(block.topics.length)) {
        return;
      }

      onChange({ expandType });
    },
    [onChange, block.topics.length]
  );

  const handleImageSizeChange = useCallback(
    (imageSize: ClickExpandImageSize) => {
      onChange({ imageSize });
    },
    [onChange]
  );

  const handleTitleLevelChange = useCallback(
    (nextTitleLevel: ClickExpandTitleLevel) => {
      onChange({ titleLevel: nextTitleLevel });
    },
    [onChange]
  );

  const handleTopicTitleChange = useCallback(
    (topicId: string, title: string) => {
      onChange({
        topics: updateById(block.topics, topicId, (topic) => ({ ...topic, title })),
      });
    },
    [onChange, block.topics]
  );

  const handleTopicContentChange = useCallback(
    (topicId: string, content: JSONContent) => {
      onChange({
        topics: updateById(block.topics, topicId, (topic) => ({
          ...topic,
          content,
        })),
      });
    },
    [onChange, block.topics]
  );

  const handleAddTopic = useCallback(() => {
    if (block.topics.length >= maxTopics) return;
    const newTopic: ClickExpandTopic = {
      id: crypto.randomUUID(),
      title: `Topic ${block.topics.length + 1}`,
      content: EMPTY_RICH_TEXT_DOC,
    };
    onChange({ topics: [...block.topics, newTopic] });
  }, [maxTopics, onChange, block.topics]);

  const handleRemoveTopic = useCallback(
    (topicId: string) => {
      if (block.topics.length <= CLICK_EXPAND_MIN_TOPICS) return;
      onChange({ topics: block.topics.filter((t) => t.id !== topicId) });
    },
    [onChange, block.topics]
  );

  const handleImageInsert = useCallback(
    (data: ImageInsertData) => {
      if (!imageModalTopicId) return;
      if (!data.src) {
        onChange({
          topics: updateById(block.topics, imageModalTopicId, (topic) => ({
            ...topic,
            imageUrl: undefined,
            imageAlt: undefined,
            imageLibraryItemId: undefined,
          })),
        });
        clearImageModalTarget();
        return;
      }

      const trimmedAlt = data.alt.trim();
      const safeAlt = trimmedAlt.length >= 5 ? trimmedAlt : 'Image';
      onChange({
        topics: updateById(block.topics, imageModalTopicId, (topic) => ({
          ...topic,
          imageUrl: data.src,
          imageAlt: safeAlt,
          imageLibraryItemId: data.libraryItemId,
        })),
      });
      clearImageModalTarget();
    },
    [block.topics, clearImageModalTarget, imageModalTopicId, onChange]
  );

  const handleImageRemove = useCallback(
    (topicId: string) => {
      onChange({
        topics: updateById(block.topics, topicId, (topic) => ({
          ...topic,
          imageUrl: undefined,
          imageAlt: undefined,
          imageLibraryItemId: undefined,
        })),
      });
    },
    [onChange, block.topics]
  );

  const imageModalInitialImage = useMemo(() => {
    if (!imageModalTopicId) {
      return undefined;
    }

    const topic = block.topics.find((entry) => entry.id === imageModalTopicId);
    if (!topic?.imageUrl) {
      return undefined;
    }

    return {
      src: topic.imageUrl,
      alt: topic.imageAlt,
    };
  }, [block.topics, imageModalTopicId]);

  return (
    <div ref={containerRef} style={editorStyles.container}>
      <style>{clickExpandEditorInnerStyles}</style>

      <div style={editorStyles.header}>
        {!translationMode ? <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => handleTitleLevelChange(level as ClickExpandTitleLevel)}
          tokens={tokens}
        /> : null}
        <input
          type="text"
          value={block.title}
          placeholder="Enter title..."
          onChange={(e) => onChange({ title: e.target.value })}
          onFocus={() => handleFieldFocus('title')}
          onBlur={handleFieldBlur}
          style={getTitleInputStyle(titleLevel, tokens)}
          autoFocus={autoFocus}
        />
        <div>
          <ClickExpandRichFieldEditor
            content={block.description}
            placeholder="Enter description (optional)..."
            isFocused={focusedField === 'description'}
            onFocus={() => handleFieldFocus('description')}
            onBlur={handleFieldBlur}
            onContentChange={handleDescriptionChange}
            autoFocus={false}
            containerRef={containerRef}
            editorStyle={editorStyles.descriptionEditor}
          />
        </div>
      </div>

      {!translationMode ? <><div style={editorStyles.expandTypeSection}>
        <span style={editorStyles.expandTypeLabel}>Layout:</span>
        <button
          type="button"
          style={{
            ...editorStyles.expandTypeButton,
            ...(block.expandType === 'accordion'
              ? editorStyles.expandTypeButtonActive
              : editorStyles.expandTypeButtonInactive),
          }}
          onClick={() => handleExpandTypeChange('accordion')}
        >
          Accordion
        </button>
        <button
          type="button"
          style={{
            ...editorStyles.expandTypeButton,
            ...(block.expandType === 'top-tabs'
              ? editorStyles.expandTypeButtonActive
              : editorStyles.expandTypeButtonInactive),
            ...(isTopTabsSelectionDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          }}
          disabled={isTopTabsSelectionDisabled}
          onClick={() => handleExpandTypeChange('top-tabs')}
          title={isTopTabsSelectionDisabled
            ? `Top Tabs supports up to ${CLICK_EXPAND_MAX_TOPICS['top-tabs']} topics.`
            : undefined}
        >
          Top Tabs
        </button>
      </div>

      <div style={editorStyles.imageSizeSection}>
        <span style={editorStyles.expandTypeLabel}>Image Size:</span>
        <button
          type="button"
          style={{
            ...editorStyles.expandTypeButton,
            ...((block.imageSize ?? 'small') === 'small'
              ? editorStyles.expandTypeButtonActive
              : editorStyles.expandTypeButtonInactive),
          }}
          onClick={() => handleImageSizeChange('small')}
        >
          Small
        </button>
        <button
          type="button"
          style={{
            ...editorStyles.expandTypeButton,
            ...((block.imageSize ?? 'small') === 'large'
              ? editorStyles.expandTypeButtonActive
              : editorStyles.expandTypeButtonInactive),
          }}
          onClick={() => handleImageSizeChange('large')}
        >
          Large
        </button>
      </div></> : null}

      <div style={editorStyles.topicsList}>
        {block.topics.map((topic, index) => (
          <ClickExpandTopicEditor
            key={topic.id}
            topic={topic}
            index={index}
            canRemoveTopic={block.topics.length > CLICK_EXPAND_MIN_TOPICS}
            focusedField={focusedField}
            onFieldFocus={handleFieldFocus}
            onFieldBlur={handleFieldBlur}
            onTitleChange={handleTopicTitleChange}
            onContentChange={handleTopicContentChange}
            onImageInsert={(topicId) => openImageModalTarget(topicId)}
            onImageRemove={handleImageRemove}
            onRemove={handleRemoveTopic}
            containerRef={containerRef}
            styles={editorStyles}
            tokens={tokens}
            translationMode={translationMode}
          />
        ))}

        {!translationMode ? <button
          type="button"
          style={{
            ...editorStyles.addTopicButton,
            ...(hasReachedMaxTopics ? editorStyles.addTopicButtonDisabled : {}),
          }}
          disabled={hasReachedMaxTopics}
          onClick={handleAddTopic}
          onMouseEnter={(e) => {
            if (block.topics.length < maxTopics) {
              e.currentTarget.style.borderColor = tokens.brand.primary;
              e.currentTarget.style.color = tokens.brand.primary;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = tokens.borders.default;
            e.currentTarget.style.color = tokens.text.muted;
          }}
        >
          {hasReachedMaxTopics
            ? `Maximum ${maxTopics} topics reached`
            : '+ Add Topic'}
        </button> : null}
      </div>

      {!translationMode ? <ImageInsertModal
        isOpen={isImageModalOpen}
        onClose={clearImageModalTarget}
        onInsert={handleImageInsert}
        initialImage={imageModalInitialImage}
      /> : null}
    </div>
  );
}

ClickExpandEditor.displayName = 'ClickExpandEditor';
